import { buildTime } from '../../buildTime';

function WelcomeView() {
    return (
        // flex-grow on the root so the inner `height: 0` scroll area gets a
        // real height from the pane wrapper (every other tab's root grows
        // vertically too).
        <div className={'layout vertical flex-grow welcome'}>
            <div className={'layout vertical flex-grow'} style={{gap: 10, height: 0, overflowY: 'auto'}}>
                <div className={'layout vertical gap box'}>
                    <p className={'text header'}>Introduction</p>
                    <p>Welcome to the Rift Editor. We built this site so we could tweak all the rules and parameters
                        for Rift quickly, intuitively, and without touching a line of code.</p>
                    <p>It has a second purpose too: to allow you, and any user, to make your own version of the game;
                        whether it’s a minor variant or a complete redesign. You can not only change the rules but also
                        completely reskin the graphics if you wish – and it’ll all be housed in your own <b>branch</b> (or
                        branches, if you want to create more than one), where you can safely edit as much as you
                        want without impacting the main version of the game, which is housed in the <b>Master</b> branch.</p>
                    <p>There are tiered levels of access in the Editor. By default, you will be assigned
                        “<b>Tester</b>” access, which will allow you to create your own branch. Only users with
                        “<b>Admin</b>” access can change the Master branch.</p>
                    <p>While every tab within the Editor comes with its own brief explainer (accessed by clicking the
                        “i” icon on the right side of the currently selected tab), the Editor as a whole is based on a
                        simple drag-and-drop workflow, which works as follows: game assets are listed on the right side
                        of the tab, and you assign them by dragging them onto the thing they belong to.</p>
                    <p>For example:</p>
                    <ul>
                        <li>in the “Tech Tree” tab, dragging a unit or building onto a specific node in the tech tree
                            will mean that is unlocked by researching that tech node;</li>
                        <li>in the “Building” tab dragging a unit onto a building will mean that unit is producable in
                            that building,</li>
                        <li>in the “Tiles” tab dragging a resource onto a tile type means that resource is eligible to
                            spawn on that tile, and so on.</li>
                    </ul>
                    <p>In each tab, you can also add or remove new techs, buildings, units, resources and so on.</p>
                    <div className={'layout horizontal gap'}>
                        <button onClick={() => window.open('https://rift-test.xp3games.com')}>Open Test Game</button>
                        <button onClick={() => window.open('https://rift.xp3games.com')}>Play Live Rift</button>
                    </div>
                    <p className={'text-right text-muted'}>Editor built at {buildTime}</p>
                </div>

                <div className={'layout vertical gap box'}>
                    <p className={'text header'}>Quick Start</p>
                    <p><b>1. Make your own branch.</b> Click the branch button at the bottom-left of the sidebar (it
                        shows the branch you're on). Type a name under <b>New Branch</b> and press <b>Add</b>. You now
                        have a private copy of everything. Leave <b>Master</b> alone — that's the real game.</p>
                    <div className={'layout horizontal gap'} style={{alignItems: 'flex-start', flexWrap: 'wrap'}}>
                        <img alt={'The branch button at the bottom-left of the sidebar'} className={'preview-img'}
                             style={{width: 320}} src={'./assets/welcome-branch-button.png'}/>
                        <img alt={'The branches window, with the New Branch name box and Add button'} className={'preview-img'}
                             style={{width: 260}} src={'./assets/welcome-branches-window.png'}/>
                    </div>
                    <br/>
                    <p><b>2. Change something.</b> Pick a tab on the left and edit away. Nothing you do can break anyone
                        else's branch.</p>
                    <br/>
                    <p><b>3. Export.</b> Your edits live in the browser until you press <b>Export</b>. That publishes
                        them to your branch and gives it a new version number.</p>
                    <img alt={'The Export button at the bottom-left of the sidebar'} className={'preview-img'}
                         style={{width: 320}} src={'./assets/welcome-export-button.png'}/>
                    <br/>
                    <p><b>4. Play it.</b> Open the <b>test</b> game — not the live one, which won't have your branch.
                        The <b>LogicBranch</b> dropdown is on the main menu itself, so there's nothing to open: just
                        pick your branch from it. Branches are listed as “Name vVersion”, so check the version matches
                        the one you just exported. If your branch or your new version isn't there yet, press the
                        refresh button beside the dropdown and look again.</p>
                    <img alt={'The LogicBranch dropdown on the game\'s main menu, listing branches as Name vVersion'}
                         className={'preview-img'} style={{width: 380}} src={'./assets/in-game-branch-select.png'}/>
                    <div className={'layout horizontal gap'}>
                        <button onClick={() => window.open('https://rift-test.xp3games.com')}>Open Test Game</button>
                    </div>
                </div>

                <div className={'layout vertical gap box'}>
                    <p className={'text header'}>Finding Your Way Around</p>
                    <p>Tabs are grouped under <b>Welcome</b>, <b>Gameplay</b> and <b>Assets</b> — click a group heading
                        for a summary of what its tabs cover. The chevron by the title collapses the sidebar when you
                        need more room.</p>
                    <br/>
                    <p><b>Every tab explains itself.</b> Click the tab you're already on (or the “i” icon on it) to open
                        its notes. Start there rather than guessing — the notes cover what each field actually does.</p>
                    <br/>
                    <p><b>Drag and drop.</b> The list of things you can assign always sits to the right of what it
                        assigns to. Drag an attribute onto a unit to give the unit that attribute; drag a unit onto a
                        tech node to lock it behind that tech. To undo an assignment, drag it back onto the list it came
                        from, or use its trash icon.</p>
                    <br/>
                    <p><b>Undo and redo</b> are the two arrow buttons at the bottom-left, next to the branch button.</p>
                    <br/>
                    <p><b>Jump to a definition.</b> Ctrl-click (Cmd-click on a Mac) the name of a unit, building, tile,
                        resource or attribute anywhere it's mentioned to jump to the tab where it's defined.</p>
                    <br/>
                    <p>Units, buildings and attributes carry a status marker:</p>
                    <p><span className={'fa-solid fa-check-circle'} style={{color: "#00b262", fontSize: 13}}/> Active — the
                        game client uses it.</p>
                    <p><span className={'fa-solid fa-circle-x'} style={{color: "#d03700", fontSize: 13}}/> Inactive — it
                        exists here but the game ignores it, so it can't be tested yet.</p>
                </div>

                <div className={'layout vertical gap box'}>
                    <p className={'text header'}>Saving &amp; Publishing</p>
                    <p><b>Export</b> (or Ctrl/Cmd+S) is the only thing that saves your work. Until you press it, your
                        changes exist only in this browser tab.</p>
                    <p>The line above the button tells you where you stand: <b>Unsaved</b> or <b>No Changes</b>, and the
                        branch's current version. Every export adds one to that version.</p>
                    <br/>
                    <p><b>Switching branches throws away unsaved changes</b> — you'll be warned first. Creating a new
                        branch while you have unsaved changes instead carries them into the new branch as v1, leaving the
                        branch you were on untouched.</p>
                    <br/>
                    <p>If someone else exported to your branch while you were working, you'll get a warning before you
                        overwrite them. If in doubt, cancel and re-load the branch.</p>
                    <br/>
                    <p><b>Going live.</b> Export only reaches the test game. Getting a branch into the live game that
                        real players use is a separate, deliberate step, done from the <b>Settings</b> tab by an admin.</p>
                </div>

                <div className={'layout vertical gap box'}>
                    <p className={'text header'}>Extras</p>
                    <p><b>Compare branches.</b> The branches window has a <b>Compare with another branch…</b> button that
                        shows exactly what differs between two branches.</p>
                    <br/>
                    <p><b>Live Preview.</b> The button above undo/redo broadcasts your artwork, image packs, sounds and
                        tech-tree visuals straight into your own running game — no export needed. It only ever reaches
                        your own sessions, and only changes how things look, never the rules.</p>
                </div>

                <div className={'layout vertical gap box'}>
                    <p className={'text header'}>If Something Is Missing</p>
                    <p>What you can do depends on the role an admin has given your account. New accounts start as
                        <b> Tester</b>, which covers everything above.</p>
                    <p className={'muted-text'}>Viewer — read only. Guest — edit here, but nothing can be saved.
                        Tester — create branches, export, upload artwork, use Live Preview. Admin — also edit Master,
                        publish live, and manage users.</p>
                    <p>So if a button described here isn't on your screen, it's almost certainly your role rather than a
                        fault. Ask an admin.</p>
                </div>
            </div>
        </div>
    )
}

export default WelcomeView;
